<template>
  <section class="review-awards y2023" :class="{ desktop: $isDesktop }">
    <div
      v-observe-visibility="{
        callback: heroVisibilityChanged,
        throttle: 300,
        throttleOptions: {
          leading: 'hidden',
        },
      }"
      class="hero"
    >
      <div>
        <h1>Pitchup.com <br />Review Awards <br />2023</h1>
        <review-awards-pages-nav />
      </div>
    </div>

    <nav ref="page-navigation" :class="{ pinned: navigationOffscreen }">
      <ul class="nav-list">
        <li><a href="#how-it-works">How it works</a></li>
        <li><a href="#overall-winner">Overall winner</a></li>
        <li><a href="#runner-up">Runner up</a></li>
        <li><a href="#top-5">Top 5 sites</a></li>
        <li><a href="#regional-winners">Regional winners</a></li>
        <li><a href="#top-rated-by-theme">Top rated by theme</a></li>
      </ul>
    </nav>

    <section id="how-it-works">
      <div class="intro">
        <p>
          We're thrilled to share our shortlist of the best camping, glamping
          and caravan sites based on nearly 200,000 customer reviews last year.
        </p>
        <p>
          To feature in the list, sites must have achieved an average rating of
          8 or above across at least 20 reviews in 2023. We keep the criteria
          simple and rely solely on customer feedback to ensure the list is
          unbiased and reflects real user experiences.
        </p>
        <p>
          We want to say a huge congratulations to the winning sites. Their
          outstanding average rating is the result of months of glowing reviews
          on Pitchup.com, showing that they provided a truly exceptional guest
          experience.
        </p>
      </div>
      <div v-if="$isDesktop" class="map-wrapper">
        <UkRegionsMap />
      </div>
    </section>

    <section id="overall-winner" class="campsite-card">
      <h2>Our top-rated campsite</h2>
      <a
        class="campsite winner"
        target="_blank"
        href="https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/King_s_Lynn/strattons-farm-campsite/"
      >
        <div class="cs-img">
          <img
            loading="lazy"
            width="500"
            height="auto"
            alt="Strattons Farm Campsite - Overall winner"
            src="https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/3/image/private/s--ztIMI2ok--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1716216079/strattons-farm-campsite/1405445.jpg"
            :sizes="$isDesktop ? '640px' : '88vw'"
            :srcset="`https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/3/image/private/s--ztIMI2ok--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1716216079/strattons-farm-campsite/1405445.jpg 800w, https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=1200,h=900/images/3/image/private/s--ztIMI2ok--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1716216079/strattons-farm-campsite/1405445.jpg 1200w`"
          />
        </div>
        <div class="contents">
          <img
            src="@/assets/images/first-place.png"
            alt=""
            width="100"
            height="140"
            class="rosette"
            loading="lazy"
          />
          <img
            src="@/assets/images/pitchup-logo.png"
            alt=""
            width="80"
            height="70"
            loading="lazy"
          />
          <h2>Strattons Farm Campsite</h2>
          <p>
            Scoring 10/10 in 57 reviews, Strattons Farm Campsite is the best of
            the best. Family-friendly and impeccably maintained, this Norfolk
            site ticks a lot of boxes: spotless facilities, plenty to do
            (including a fishing lake, swimming pool and play area) and helpful,
            welcoming hosts.
          </p>
        </div>
      </a>
      <aside>
        <div class="triangle" />
        <img
          src="@/assets/images/quotemark-66.png"
          alt=""
          width="60"
          height="41"
          class="quotemark"
          loading="lazy"
        />
        <p>
          "This win means so much to us", said owners Jane and Dave, "Since we
          only went up on Pitchup last year, to win this award is a great
          surprise! We get a lot of good feedback, but winning is quite an
          achievement."
        </p>
        <span>
          <p>
            Site manager Liam added "We always maintain a high standard in terms
            of the facilities and we make sure we give customers a personable
            service, liaising with everyone before they arrive and when they
            depart. This win really shows we're doing a good job. We're grateful
            that our customers feel we're providing such a good experience."
          </p>
          <p class="author">Owners Jane and Dave</p>
        </span>
        <img
          src="@/assets/images/quotemark-99.png"
          alt=""
          width="60"
          height="41"
          class="quotemark close"
          loading="lazy"
        />
      </aside>
    </section>

    <section id="runner-up" class="campsite-card">
      <h2>Runner up</h2>
      <a
        href="https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/llandrinio/sychpwll-centre/"
        target="_blank"
        class="campsite runner-up with-border"
      >
        <div class="cs-img">
          <img
            loading="lazy"
            width="500"
            height="auto"
            alt="Sychpwll Centre - Runner up"
            src="https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/3/image/private/s--TW4RYWFv--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1661531042/sychpwll-centre/1050176.jpg"
            :sizes="$isDesktop ? '640px' : '88vw'"
            :srcset="`https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/3/image/private/s--TW4RYWFv--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1661531042/sychpwll-centre/1050176.jpg 800w, https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=1200,h=900/images/3/image/private/s--TW4RYWFv--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1661531042/sychpwll-centre/1050176.jpg 1200w`"
          />
        </div>
        <div class="contents">
          <img
            src="@/assets/images/second-place.png"
            alt=""
            width="100"
            height="140"
            class="rosette"
            loading="lazy"
          />
          <img
            src="@/assets/images/pitchup-logo.png"
            alt=""
            width="80"
            height="70"
            loading="lazy"
          />
          <h2>Sychpwll Centre</h2>
          <p>
            Scoring 10/10 from 56 reviews, this Powys site is an extremely close
            second place. With several country pubs with good food and drink
            close by, a wildflower meadow surrounding the pitches and a
            beautifully decorated communal kitchen/diner, it's easy to see why
            this eco site is a firm fan favourite.
          </p>
        </div>
      </a>
    </section>

    <section id="top-5" class="top-5">
      <h2>
        <img
          loading="lazy"
          src="@/assets/images/pitchup-favicon.svg"
          alt=""
          width="36"
          height="32"
        />Top 5 Campsites 2023
      </h2>
      <div class="top-5-data">
        <table class="top-sites-table">
          <thead>
            <tr>
              <th>Site name</th>
              <th>Region</th>
              <th>Pitch types</th>
              <th class="center-align">Rating</th>
            </tr>
          </thead>
          <tbody>
            <tr
              onclick="window.open('https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/King_s_Lynn/strattons-farm-campsite/', '_blank')"
            >
              <td>
                <a
                  target="_blank"
                  href="https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/King_s_Lynn/strattons-farm-campsite/"
                  >Strattons Farm Campsite</a
                >
              </td>
              <td>East Anglia</td>
              <td>
                <div
                  class="category-icons horizontal-spacing"
                  data-v-4f128b38=""
                >
                  <div class="site-category">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/tent-pitches.svg"
                      alt="Tent pitches"
                      width="20"
                      height="20"
                      class="category-icon tent-pitches"
                    />
                    <span class="count">10</span>
                  </div>
                  <div class="site-category">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/touring-pitches.svg"
                      alt="Touring caravan pitches"
                      width="20"
                      height="20"
                      class="category-icon touring-pitches"
                    />
                    <span class="count">10</span>
                  </div>
                  <div class="site-category">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/motorhomes.svg"
                      alt="Motorhome pitches"
                      width="20"
                      height="20"
                      class="category-icon motorhomes"
                    />
                    <span class="count">10</span>
                  </div>
                </div>
              </td>
              <td class="center-align">
                <pu-rating-bubble :rating="10" :rate-count="100" />
              </td>
            </tr>
            <tr
              onclick="window.open('https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/llandrinio/sychpwll-centre/', '_blank')"
            >
              <td>
                <a
                  target="_blank"
                  href="https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/llandrinio/sychpwll-centre/"
                  >Sychpwll Centre</a
                >
              </td>
              <td>Mid Wales</td>
              <td>
                <div
                  class="category-icons horizontal-spacing"
                  data-v-4f128b38=""
                >
                  <div class="site-category" data-v-4f128b38="">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/lodges-for-hire.svg"
                      alt="Lodges, cabins, pods or huts"
                      width="20"
                      height="20"
                      class="category-icon lodges-for-hire"
                      data-v-4f128b38=""
                    />
                    <span class="count" data-v-4f128b38="">5</span>
                  </div>
                  <div class="site-category" data-v-4f128b38="">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/motorhomes.svg"
                      alt="Motorhome pitches"
                      width="20"
                      height="20"
                      class="category-icon motorhomes"
                      data-v-4f128b38=""
                    />
                    <span class="count" data-v-4f128b38="">5</span>
                  </div>
                  <div class="site-category" data-v-4f128b38="">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/tent-pitches.svg"
                      alt="Tent pitches"
                      width="20"
                      height="20"
                      class="category-icon tent-pitches"
                      data-v-4f128b38=""
                    />
                    <span class="count" data-v-4f128b38="">20</span>
                  </div>
                  <div class="site-category" data-v-4f128b38="">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/rent-a-tent.svg"
                      alt="Rent a tent"
                      width="20"
                      height="20"
                      class="category-icon rent-a-tent"
                      data-v-4f128b38=""
                    />
                    <span class="count" data-v-4f128b38="">1</span>
                  </div>
                </div>
              </td>
              <td class="center-align">
                <pu-rating-bubble :rating="10" :rate-count="100" />
              </td>
            </tr>
            <tr
              onclick="window.open('https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/Woodbridge/brick-kiln-campsite/', '_blank')"
            >
              <td>
                <a
                  target="_blank"
                  href="https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/Woodbridge/brick-kiln-campsite/"
                  >Brick Kiln Campsite</a
                >
              </td>
              <td>East Anglia</td>
              <td>
                <div class="category-icons horizontal-spacing">
                  <div class="site-category">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/tent-pitches.svg"
                      alt="Tent pitches"
                      width="20"
                      height="20"
                      class="category-icon tent-pitches"
                    />
                    <span class="count">30</span>
                  </div>
                  <div class="site-category">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/lodges-for-hire.svg"
                      alt="Lodges, cabins, pods or huts"
                      width="20"
                      height="20"
                      class="category-icon lodges-for-hire"
                    />
                    <span class="count">2</span>
                  </div>
                  <div class="site-category">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/touring-pitches.svg"
                      alt="Touring caravan pitches"
                      width="20"
                      height="20"
                      class="category-icon touring-pitches"
                    />
                    <span class="count">30</span>
                  </div>
                  <div class="site-category">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/motorhomes.svg"
                      alt="Motorhome pitches"
                      width="20"
                      height="20"
                      class="category-icon motorhomes"
                    />
                    <span class="count">30</span>
                  </div>
                </div>
              </td>
              <td class="center-align">
                <pu-rating-bubble :rating="10" :rate-count="100" />
              </td>
            </tr>
            <tr
              onclick="window.open('https://www.pitchup.com/campsites/England/North_East/Co_Durham/Frosterley/woodcroft-farm/', '_blank')"
            >
              <td>
                <a
                  target="_blank"
                  href="https://www.pitchup.com/campsites/England/North_East/Co_Durham/Frosterley/woodcroft-farm/"
                  >Woodcroft Farm</a
                >
              </td>
              <td>North East England</td>
              <td>
                <div class="category-icons horizontal-spacing">
                  <div class="site-category">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/lodges-for-hire.svg"
                      alt="Lodges, cabins, pods or huts"
                      width="20"
                      height="20"
                      class="category-icon lodges-for-hire"
                    />
                    <span class="count">3</span>
                  </div>
                </div>
              </td>
              <td class="center-align">
                <pu-rating-bubble :rating="10" :rate-count="100" />
              </td>
            </tr>
            <tr
              onclick="window.open('https://www.pitchup.com/campsites/England/North_West/Cumbria/Cockermouth/cragg-farm-camping-pods/', '_blank')"
            >
              <td>
                <a
                  target="_blank"
                  href="https://www.pitchup.com/campsites/England/North_West/Cumbria/Cockermouth/cragg-farm-camping-pods/"
                  >Cragg Farm Camping Pods</a
                >
              </td>
              <td>North West England</td>
              <td>
                <div class="category-icons horizontal-spacing">
                  <div class="site-category">
                    <img
                      loading="lazy"
                      src="@/assets/images/category-icons/lodges-for-hire.svg"
                      alt="Lodges, cabins, pods or huts"
                      width="20"
                      height="20"
                      class="category-icon lodges-for-hire"
                    />
                    <span class="count">3</span>
                  </div>
                </div>
              </td>
              <td class="center-align">
                <pu-rating-bubble :rating="10" :rate-count="100" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <RegionalWinners id="regional-winners" :data="RegionalWinnersUK2023Data" />

    <section id="top-rated-by-theme" class="top-theme">
      <section class="campsite-card">
        <h2>Top-rated sites by theme</h2>
        <a
          target="_blank"
          href="https://www.pitchup.com/campsites/England/Central/Gloucestershire/Coleford/bracelands-caravan-camping-park/"
          class="campsite with-border"
        >
          <div class="cs-img">
            <img
              loading="lazy"
              width="500"
              height="auto"
              alt="Bracelands Campsite - The most peaceful campsite"
              src="https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/1/image/private/s--vS-t6UZU--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1669294274/bracelands-caravan-camping-park/1097863.jpg"
              :sizes="$isDesktop ? '640px' : '88vw'"
              :srcset="`https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/1/image/private/s--vS-t6UZU--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1669294274/bracelands-caravan-camping-park/1097863.jpg 800w, https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=1200,h=900/images/1/image/private/s--vS-t6UZU--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1669294274/bracelands-caravan-camping-park/1097863.jpg 1200w`"
            />
          </div>
          <div class="contents">
            <h2>The most peaceful campsite</h2>
            <p>
              Bracelands Campsite in Gloucestershire was dubbed 'peaceful' by a
              whopping 376 reviewers in 2023, more than any other site.
              Wonderfully spacious and encircled by trees, this wildlife haven
              has a no music after 10 pm rule to ensure all guests get a good
              night's sleep.
            </p>
          </div>
        </a>

        <a
          target="_blank"
          href="https://www.pitchup.com/campsites/Scotland/Scotland/Highlands/Fort_William/ben-nevis-holiday-park/"
          class="campsite with-border"
        >
          <div class="cs-img">
            <img
              loading="lazy"
              width="500"
              height="auto"
              alt="Ben Nevis Holiday Park - The most luxurious campsite"
              src="https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/2/image/private/s--gt4dH5hi--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1658761954/ben-nevis-holiday-park/997703.jpg"
              :sizes="$isDesktop ? '640px' : '88vw'"
              :srcset="`https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/2/image/private/s--gt4dH5hi--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1658761954/ben-nevis-holiday-park/997703.jpg 800w, https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=1200,h=900/images/2/image/private/s--gt4dH5hi--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1658761954/ben-nevis-holiday-park/997703.jpg 1200w`"
            />
          </div>
          <div class="contents">
            <h2>The most luxurious campsite</h2>
            <p>
              The winner of the most luxurious site in 2023, was Ben Nevis
              Holiday Park in Scotland, with 67 people referring to it as
              'luxury' in their reviews. With immaculately kept pitches, grounds
              and facilities, and even luxury hand wash and hand cream in the
              loos, it's no surprise that this site has nabbed the top spot.
            </p>
          </div>
        </a>

        <a
          target="_blank"
          href="https://www.pitchup.com/campsites/England/Central/Derbyshire/Bakewell/haddon-grove-farm-camp-site/"
          class="campsite with-border"
        >
          <div class="cs-img">
            <img
              loading="lazy"
              width="500"
              height="auto"
              alt="Haddon Grove Farm Campsite - The best on a budget"
              src="https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/5/image/private/s--MZzuOXEE--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1677078058/haddon-grove-farm-camp-site/1121690.jpg"
              :sizes="$isDesktop ? '640px' : '88vw'"
              :srcset="`https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=800,h=600/images/5/image/private/s--MZzuOXEE--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1677078058/haddon-grove-farm-camp-site/1121690.jpg 800w, https://www.pitchup.com/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=50,w=1200,h=900/images/5/image/private/s--MZzuOXEE--/c_limit,h_2400,w_3200/e_improve,fl_progressive/q_auto/b_rgb:000,g_south_west,l_pitchup.com_wordmark_white_watermark,o_15/v1677078058/haddon-grove-farm-camp-site/1121690.jpg 1200w`"
            />
          </div>
          <div class="contents">
            <h2>The best on a budget</h2>
            <p>
              Haddon Grove Farm Campsite was named a perfect site for
              budget-conscious backpackers by 129 reviewers last year. With
              prices as low as £20 per night, a relaxed 'down-to-earth' feel,
              and easy access to the Derbyshire Dales, guests felt this site
              provided excellent value for money.
            </p>
          </div>
        </a>
      </section>
    </section>
    <nuxt-to-meta :nuxt2meta="head" />
  </section>
</template>

<script setup lang="ts">
import UkRegionsMap from '~/apps/maps/components/UkRegionsMap.vue'
import RegionalWinners from '~/components/reviews/RegionalWinners.vue'
import SCHEMA_ORGANIZATION from '../../../json/schemaOrganization.json'

definePageMeta({
  layout: 'default',
})

const { $isDesktop } = useNuxtApp()

const navigationOffscreen = ref(false)
const heroVisibilityChanged = (isVisible: boolean) => {
  navigationOffscreen.value = !isVisible
}

const head = computed(() => {
  const images = import.meta.glob<{ default: string }>(
    '@/assets/images/review-awards-og.png',
    {
      query: '?url',
      eager: true,
    },
  )
  return {
    htmlAttrs: {
      class: 'smooth-scroll',
    },
    title: 'Pitchup.com review awards',
    script: [schemaOrganization.value],
    meta: [
      {
        hid: 'og:title',
        property: 'og:title',
        content: 'Pitchup.com review awards',
      },
      {
        hid: 'og:url',
        property: 'og:url',
        content: 'https://www.pitchup.com/review-awards/',
      },
      {
        hid: 'description',
        name: 'description',
        property: 'og:description',
        content:
          "Pitchup.com's review awards are based on nearly 200,000 customer reviews from 2023. Here's our shortlist of the top rated campsites.",
      },
      {
        hid: 'og:image',
        property: 'og:image',
        content: images['/assets/images/review-awards-og.png'].default,
      },
      {
        hid: 'og:image:width',
        property: 'og:image:width',
        content: '1200',
      },
      {
        hid: 'og:image:height',
        property: 'og:image:height',
        content: '630',
      },
    ],
  }
})

const schemaOrganization = computed(() => ({
  type: 'application/ld+json',
  body: true,
  json: SCHEMA_ORGANIZATION,
  hid: 'schema-organization',
}))

const RegionalWinnersUK2023Data = [
  {
    areaTitle: 'Mid Wales',
    area: 'mid-wales',
    intro:
      "Mid Wales came out on top in terms of overall average rating, gaining a score of 9.5. With the Cambrian Mountains and the Ceredigion coast, it's easy to see why this beautiful spot might top the charts for outdoor holidays.",
    winners: [
      {
        name: 'Sychpwll Centre',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/llandrinio/sychpwll-centre/',
      },
      {
        name: 'Wild Meadow Camping',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Ceredigion/Plwmp/wild-meadow-camping_/',
      },
      {
        name: 'Bryndu Caravan and Camping',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/llyswen/bryndu_caravan_and_camping/',
      },
      {
        name: 'Lower Porthamel Camping',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Talgarth/lower-porthamel-camping/',
      },
      {
        name: 'Pwllyn Farm Camping',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/libanus/pwllyn-farm-camping/',
      },
      {
        name: 'Borders Hideaway Holiday Home Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/hay-wye/borders-hideaway-holiday-home-park/',
      },
      {
        name: 'Riverbend Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Welshpool/riverbend-caravan-park/',
      },
      {
        name: 'Woodlands Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Ceredigion/Aberystwyth/woodlands-caravan-park_/',
      },
      {
        name: 'Meadow Springs Country and Leisure Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/caersws/meadow-springs-country-leisure-park/',
      },
      {
        name: 'Black Mountain View Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/hay-wye/black-mountain-view-caravan-park/',
      },
      {
        name: 'Irfon Valley Pods and Camping',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Llangammarch_Wells/irfon_valley_pods_camping/',
      },
      {
        name: 'Camp Crick',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Crickhowell/camp-crick/',
      },
      {
        name: 'Wern Mill Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Ceredigion/New_Quay/wern_mill_caravan_park/',
      },
      {
        name: 'Cardigan Island Coastal Farm Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Ceredigion/Cardigan/cardigan-island-coastal-farm-park/',
      },
      {
        name: 'Parc Farm Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Penybontfawr/parc_farm_caravan_park/',
      },
      {
        name: 'Gwerniago Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Machynlleth/gwerniago-campsite/',
      },
      {
        name: 'Gellie Camping',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Ceredigion/Plwmp/gellie-liahona-campsite/',
      },
      {
        name: 'Rockbridge Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Presteigne/rockbridge-park/',
      },
      {
        name: 'Spring Rock Holidays',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Llandrindod_Wells/spring-rock-fishery/',
      },
      {
        name: 'Henstent Park',
        url: 'https://www.pitchup.com/campsites/Wales/Mid-Wales/Powys/Llangynog/henstent_park/',
      },
    ],
  },
  {
    areaTitle: 'North Wales',
    area: 'north-wales',
    intro:
      "A close second place scoring 9.4, North Wales, home to Eryri National Park and the Clwydian Range, is a walker's paradise. Visitors will not be disappointed by the views on offer here.",
    winners: [
      {
        name: 'Clwydian Holidays',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Denbighshire/Ruthin/clwydian_wigwams/',
      },
      {
        name: 'Maes Carafanau Llanbenwch Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Denbighshire/llanfair_dyffryn_clwyd/maes_carafanau_llanbenwch_caravan_park/',
      },
      {
        name: 'The Paddocks Caravan and Camping',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Gwynedd/Criccieth/the-paddocks-caravan-and-camping/',
      },
      {
        name: 'Campio Andy Camping',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Isle_of_Anglesey/Newborough/campio-andy-camping/',
      },
      {
        name: 'Presaddfed',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Isle_of_Anglesey/Bodedern/presaddfed/',
      },
      {
        name: 'New Inn',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Denbighshire/Llangollen/new-inn/',
      },
      {
        name: 'Haulfryn Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Flintshire/Holywell/haulfryn_caravan_park/',
      },
      {
        name: 'Bach Wen',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Gwynedd/clynnog-fawr/bach-wen/',
      },
      {
        name: 'Bryn Ednyfed Caravan Site',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Isle_of_Anglesey/Holyhead/bryn_ednyfed_caravan_site/',
      },
      {
        name: 'Ceiriog Valley Park',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Denbighshire/Llangollen/ceiriog-valley-park/',
      },
      {
        name: 'Plas Farm Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Conwy/Abergele/plas-farm-caravan-park/',
      },
      {
        name: 'Camp Stesion',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Gwynedd/Trawsfynydd/camp-stesion/',
      },
      {
        name: 'Llain Ffynnon Camping',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Isle_of_Anglesey/Holyhead/llain-ffynnon-camping/',
      },
      {
        name: 'Pentre Du Camping Club',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Conwy/llysfaen/pentre-du-farm/',
      },
      {
        name: 'Pen y Bwlch',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Gwynedd/Caernarfon/pen-y-bwlch/',
      },
      {
        name: 'Llwyn yr Helm Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Gwynedd/Dolgellau/llwyn_yr_helm_caravan_park/',
      },
      {
        name: 'Pwllgoed Caravan Site',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Gwynedd/Tudweiliog/pwllgoed-caravan-site/',
      },
      {
        name: 'Maes Tywyn Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Isle_of_Anglesey/Newborough/maes_tywyn_campsite/',
      },
      {
        name: 'Ynyshir Farm',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Flintshire/Holywell/ynyshir-farm/',
      },
      {
        name: 'Corn Bwch',
        url: 'https://www.pitchup.com/campsites/Wales/North-Wales/Denbighshire/St_Asaph/corn-bwch/',
      },
    ],
  },
  {
    areaTitle: 'the North West',
    area: 'north-west',
    intro:
      "North West England scored 9.3/10 in 2023 overall. Looking out over the water of Windemere or up at the rocky peak of Scafell Pike, it's easy to see why this was crowned the top location in England for outdoor holidays.",
    winners: [
      {
        name: 'Cragg Farm Camping Pods',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Cockermouth/cragg-farm-camping-pods/',
      },
      {
        name: 'The Pods by the Stream at Flakebridge Farm',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/tebay/flakebridge-farm/',
      },
      {
        name: 'Strudda Bank Pod Paddock',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Seascale/strudda-bank-pod-paddock/',
      },
      {
        name: 'Lakes Yurts',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Cockermouth/lakes_yurts/',
      },
      {
        name: 'Dearden Wood',
        url: 'https://www.pitchup.com/campsites/England/North_West/Lancashire/Bury/dearden_wood/',
      },
      {
        name: 'Primrose Cottage Camping',
        url: 'https://www.pitchup.com/campsites/England/North_West/Lancashire/Chorley/primrose-cottage-camping/',
      },
      {
        name: 'Puddlemire',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/lupton/puddlemire/',
      },
      {
        name: 'Otter Moss Accommodation',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Brampton/otter-moss-accommodation/',
      },
      {
        name: 'Abbots Reading Farm',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Ulverston/abbots_reading_farm/',
      },
      {
        name: 'Kendal Rugby Club',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Kendal/kendal_rugby_club/',
      },
      {
        name: 'Moss Rose Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_West/Lancashire/Winmarleigh/fairmoss-black-hill-farm/',
      },
      {
        name: 'Irton House Farm',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Cockermouth/irton-house-farm/',
      },
      {
        name: 'Parkgate Farm Holidays',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Eskdale/parkgate_farm_holidays/',
      },
      {
        name: 'Bowland Escapes',
        url: 'https://www.pitchup.com/campsites/England/North_West/Lancashire/Preston/bowland_wild_boar_park/',
      },
      {
        name: 'Pennine View Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Kirkby_Stephen/pennine-view-caravan-park/',
      },
      {
        name: 'Donkey Creek Farm',
        url: 'https://www.pitchup.com/campsites/England/North_West/Lancashire/Preston/donkey_creek_farm_caravan_park/',
      },
      {
        name: 'The Barn Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Holmrook/the-barn-campsite/',
      },
      {
        name: 'Bluebell Wilds',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cheshire/whitchurch/blue-bell-wilds/',
      },
      {
        name: 'Wallace Lane Farm',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cumbria/Wigton/wallace-lane-farm/',
      },
      {
        name: 'Avona Escapes',
        url: 'https://www.pitchup.com/campsites/England/North_West/Cheshire/Congleton/avona-escapes/',
      },
    ],
  },
  {
    areaTitle: 'West Wales',
    area: 'west-wales',
    intro:
      'West Wales, scoring a 9.3, is celebrated for its rugged coastlines and sweeping landscapes. From the Pembrokeshire Coast National Park to the beaches of Cardigan Bay, the region is a haven for walkers, wildlife enthusiasts, and those seeking a tranquil seaside getaway.',
    winners: [
      {
        name: 'Ffynnonddewi',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Haverfordwest/ffynnonddewi/',
      },
      {
        name: 'Cosmos Camping',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Carmarthenshire/Llangadog/cosmos-camping/',
      },
      {
        name: 'Quarry Lodge Camping',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Carmarthenshire/Carmarthen/quarry-lodge-camping/',
      },
      {
        name: 'Prendergast Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Haverfordwest/prendergast-caravan-park/',
      },
      {
        name: 'Keeston Camping',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Haverfordwest/castle-farm/',
      },
      {
        name: 'Argoed Meadow Camping and Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Carmarthenshire/Newcastle_Emlyn/argoed-meadow-camping-caravan-park/',
      },
      {
        name: 'Twin Oaks Holidays',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Narberth/twin-oaks-holidays/',
      },
      {
        name: 'South Cockett Caravan and Camping Park',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Haverfordwest/south-cockett-caravan-and-camping-park/',
      },
      {
        name: 'Folly Farm Holiday Park',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Kilgetty/folly_farm_holiday_park/',
      },
      {
        name: 'Laugharne View Caravan Site',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Carmarthenshire/Carmarthen/laugharne-view-caravan-site/',
      },
      {
        name: 'Oaklea',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Narberth/oaklea_camp/',
      },
      {
        name: 'Hazeldene Caravan Park',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Carmarthenshire/Kidwelly/hazeldene_caravan_park/',
      },
      {
        name: 'Cedar Farm Camping',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/st-twynnells/cedar-camping/',
      },
      {
        name: 'Conifers Camping',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Newport/conifers-pentraeth-touring-park/',
      },
      {
        name: 'Maengwyn Hir Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/llanfyrnach/maengwyn_hir_campsite/',
      },
      {
        name: 'Church House Farm Getaways',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Carmarthenshire/Carmarthen/church-house-farm/',
      },
      {
        name: 'Nolton Haven Bay Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/nolton-haven/nolton-haven-bay-campsite/',
      },
      {
        name: 'Pelcomb Cross Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/pelcomb_cross/pelcomb_cross_campsite/',
      },
      {
        name: 'Llanungar Caravan and Camping',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/St_Davids/llanungar_caravan_camping/',
      },
      {
        name: 'Glan-Y-Mor Camping',
        url: 'https://www.pitchup.com/campsites/Wales/West-Wales/Pembrokeshire/Pembroke/glan-y-mor-farm-campsite/',
      },
    ],
  },
  {
    areaTitle: 'East Anglia',
    area: 'anglia',
    intro:
      "East Anglia scored 9.3/10 in 2023, and with its landscape of serene flatlands and picturesque waterways, it's not hard to see why! Renowned for its wildlife-rich wetlands and charming medieval towns, the region offers a peaceful retreat with ample opportunities for bird watching, boating, and soaking up the local history.",
    winners: [
      {
        name: 'Strattons Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/King_s_Lynn/strattons-farm-campsite/',
      },
      {
        name: 'Brick Kiln Campsite',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/Woodbridge/brick-kiln-campsite/',
      },
      {
        name: 'Broadland Sun Association Naturist Campsite',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/Norwich/broadland-sun-association/',
      },
      {
        name: 'Four Oaks Camping',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/King_s_Lynn/four-oaks-camping/',
      },
      {
        name: 'Bramley House Camping',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Cambridgeshire/Wisbech/bramley-house-caravan-and-camping/',
      },
      {
        name: 'The Wildings Elms Meadow',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/Aldeby/the-wildings-elms-meadow/',
      },
      {
        name: 'Nether Hall Farm',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/Ipswich/nether-hall-farm/',
      },
      {
        name: 'Hayfield Hideaways',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/Mundesley/hayfield-hideaways/',
      },
      {
        name: 'Glevering Estate',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/hacheston/glevering-estate/',
      },
      {
        name: 'Greendyke Farm',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Cambridgeshire/Wisbech/greendyke-farm/',
      },
      {
        name: 'Wheathill Campsite',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/Ipswich/wheathill-campsite/',
      },
      {
        name: 'Green Haven Camping',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/rumburgh/green-haven-camping/',
      },
      {
        name: 'Grange Farm Lodge',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/Stowmarket/grange-farm-lodge/',
      },
      {
        name: '7 Ducks',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/King_s_Lynn/7_ducks/',
      },
      {
        name: 'Snettisham Meadows',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/King_s_Lynn/snettisham-meadows/',
      },
      {
        name: 'Narborough Fisheries',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/narborough/narborough-fisheries/',
      },
      {
        name: 'Magpie Meadow',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/North_Walsham/magpie-meadow/',
      },
      {
        name: 'Spring Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/Thetford/spring-farm-campsite/',
      },
      {
        name: 'Sunnyside Campsite',
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Suffolk/cox-common/sunnyside-campsite/',
      },
      {
        name: "Forager's Rest",
        url: 'https://www.pitchup.com/campsites/England/East_Anglia/Norfolk/Norwich/foragers-rest/',
      },
    ],
  },
  {
    areaTitle: 'the North East',
    area: 'north-east',
    intro:
      "With a 9.3 average score in 2023, the North East of England has a rich heritage and natural beauty. From the historic Hadrian's Wall to the wild Northumberland National Park, the region offers a mix of dramatic landscapes and cultural landmarks.",
    winners: [
      {
        name: 'Woodcroft Farm',
        url: 'https://www.pitchup.com/campsites/England/North_East/Co_Durham/Frosterley/woodcroft-farm/',
      },
      {
        name: 'West Hale Gate Caravan Site',
        url: 'https://www.pitchup.com/campsites/England/North_East/East_Yorkshire/Burton_Fleming/west-hale-gate-caravan-site/',
      },
      {
        name: 'Old Pasture Park',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/York/old-pasture-park/',
      },
      {
        name: 'Butter Hill Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Thirsk/butter-hill-caravan-park/',
      },
      {
        name: 'Heathfield Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Harrogate/heathfield-caravan-park/',
      },
      {
        name: 'Wold Meadow',
        url: 'https://www.pitchup.com/campsites/England/North_East/East_Yorkshire/Driffield/wold-meadow/',
      },
      {
        name: 'Bonners Lodge',
        url: 'https://www.pitchup.com/campsites/England/North_East/Co_Durham/Consett/bonners_lodge/',
      },
      {
        name: 'Cotril Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/York/cotril-farm-campsite/',
      },
      {
        name: 'Southfield Touring Park',
        url: 'https://www.pitchup.com/campsites/England/North_East/East_Yorkshire/mappleton/southfield-touring-park/',
      },
      {
        name: 'Bridge House Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Skipton/bridge_house_camp_site/',
      },
      {
        name: 'Wombleton Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Kirkbymoorside/wombleton-caravan-park/',
      },
      {
        name: 'The Darling Woods Of May',
        url: 'https://www.pitchup.com/campsites/England/North_East/East_Yorkshire/Pocklington/the-darling-woods-of-may/',
      },
      {
        name: 'The Bungalow Caravan Site',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/York/the-bungalow-caravan-site/',
      },
      {
        name: 'Wild Camping Northumberland',
        url: 'https://www.pitchup.com/campsites/England/North_East/Northumberland/hedley-on-the-hill/wild-camping-northumberland/',
      },
      {
        name: 'Gateway To The Moors Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Kirkbymoorside/gateway-to-the-moors-caravan-park/',
      },
      {
        name: 'Westgate Carr Farm',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Pickering/westgate-carr-farm/',
      },
      {
        name: 'Alnham Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_East/Northumberland/Alnwick/alnham-farm-campsite/',
      },
      {
        name: 'Foxhill Park',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/Claxton/foxhill-park/',
      },
      {
        name: 'Yapham Holds Farmhouse Campsite',
        url: 'https://www.pitchup.com/campsites/England/North_East/East_Yorkshire/Pocklington/yapham-holds-farmhouse-campsite/',
      },
      {
        name: 'Flaxton Meadows',
        url: 'https://www.pitchup.com/campsites/England/North_East/North_Yorkshire/York/flaxton-meadows/',
      },
    ],
  },
  {
    areaTitle: 'the South West',
    area: 'south-west',
    intro:
      'Scoring 9.2 overall, the South West of England is renowned for its scenic beaches, rugged cliffs, and the mystical moors of Cornwall and Devon. This region provides a perfect setting for both serene retreats and active adventures.',
    winners: [
      {
        name: 'South Ford Farm Camping',
        url: 'https://www.pitchup.com/campsites/England/South_West/Devon/South_Molton/south-ford-farm-camping/',
      },
      {
        name: 'Owl Valley Glamping',
        url: 'https://www.pitchup.com/campsites/England/South_West/Devon/Bideford/owl-valley-glamping/',
      },
      {
        name: 'Edwards Farm',
        url: 'https://www.pitchup.com/campsites/England/South_West/Dorset/Shaftesbury/edwards-farm/',
      },
      {
        name: 'Reen Cross Campsite',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/Perranporth/reen-cross-campsite/',
      },
      {
        name: 'Combas Farm',
        url: 'https://www.pitchup.com/campsites/England/South_West/Devon/Croyde/combas-farm/',
      },
      {
        name: 'Little Trevothan Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/Coverack/little-trevothan-caravan-park/',
      },
      {
        name: 'Trigon Farm',
        url: 'https://www.pitchup.com/campsites/England/South_West/Dorset/Wareham/trigon_farm/',
      },
      {
        name: 'Chalk Meadow',
        url: 'https://www.pitchup.com/campsites/England/South_West/Dorset/Dorchester/chalk-meadow/',
      },
      {
        name: 'Walnut Tree Farm',
        url: 'https://www.pitchup.com/campsites/England/South_West/greater-bristol/Bristol/walnut-tree-farm/',
      },
      {
        name: 'Wimbleball Holidays',
        url: 'https://www.pitchup.com/campsites/England/South_West/Somerset/Dulverton/wimbleball-holidays/',
      },
      {
        name: 'Cripps Farm Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/South_West/Somerset/Highbridge/cripps-farm-caravan-park/',
      },
      {
        name: 'Trewall Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/downderry/trewall-farm-campsite/',
      },
      {
        name: 'The Hideout Camping and Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/South_West/Somerset/Bridgwater/the-hideout-camping-and-caravan-park/',
      },
      {
        name: 'Wideacres Camping',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/Boscastle/wideacres_camping/',
      },
      {
        name: 'Gwinear Campsite and Fishery',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/Newquay/gwinear_camping_and_fishing/',
      },
      {
        name: 'Camp De Lank',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/st-breward/camp-de-lank/',
      },
      {
        name: 'Porthtowan Tourist Park',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/Truro/porthtowan-tourist-park/',
      },
      {
        name: 'Boskenwyn Manor Farm',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/Helston/boskenwyn-manor-farm/',
      },
      {
        name: 'South Penquite Farm',
        url: 'https://www.pitchup.com/campsites/England/South_West/Cornwall/Bodmin/south-penquite-farm/',
      },
      {
        name: 'Heronshaw Meadow',
        url: 'https://www.pitchup.com/campsites/England/South_West/Dorset/Christchurch/heronshaw-meadow/',
      },
    ],
  },
  {
    areaTitle: 'the South East',
    area: 'south-east',
    intro:
      "Getting a 9.2 overall in 2023, the South East of England is a gateway to some of the country's most charming landscapes. From the rolling hills of the South Downs to the vibrant coastal towns, it's ideal for those seeking both relaxation by the sea and adventures in the countryside.",
    winners: [
      {
        name: 'Little Nash Woods Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/Kent/Canterbury/little-nash-woods-camping/',
      },
      {
        name: 'Fontmills Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/South_East/East_Sussex/Horam/fontmills-farm-campsite/',
      },
      {
        name: 'Ouse Meadow Campsite',
        url: 'https://www.pitchup.com/campsites/England/South_East/East_Sussex/Lewes/ouse_meadow_campsite/',
      },
      {
        name: 'Deaks Acres',
        url: 'https://www.pitchup.com/campsites/England/South_East/West_Sussex/Haywards_Heath/deaks-acres/',
      },
      {
        name: 'Duncombe Farm',
        url: 'https://www.pitchup.com/campsites/England/South_East/Hampshire/east-meon/duncombe-farm/',
      },
      {
        name: 'Still Acres Touring and Camping Park',
        url: 'https://www.pitchup.com/campsites/England/South_East/Kent/Maidstone/still_acres_touring_and_camping_park/',
      },
      {
        name: 'Pightle Farm',
        url: 'https://www.pitchup.com/campsites/England/South_East/Essex/elmdon/pightle-farm_/',
      },
      {
        name: 'Warminghurst Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/West_Sussex/Pulborough/warminghurst-farm-camping/',
      },
      {
        name: 'Palace Farm Camp Site',
        url: 'https://www.pitchup.com/campsites/England/South_East/Kent/Sittingbourne/palace-farm-camp-site/',
      },
      {
        name: 'Woodsman Kent Wild Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/Kent/Ashford/woodsman_kent_wild_camping/',
      },
      {
        name: 'Puddledock Woods Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/Kent/upstreet/puddledock_woods_camping/',
      },
      {
        name: 'Sands Farm Weekend Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/West_Sussex/Horsham/sands-farm-weekend-camping/',
      },
      {
        name: 'Stocks Farm Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/Essex/Braintree/stocks-farm-camping/',
      },
      {
        name: 'Cattlestone Farm',
        url: 'https://www.pitchup.com/campsites/England/South_East/West_Sussex/Billingshurst/cattlestone-farm/',
      },
      {
        name: 'In Nature Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/Hampshire/New_Milton/in-nature-camping/',
      },
      {
        name: 'Strictly Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/Kent/Tunbridge_Wells/strictly_camping/',
      },
      {
        name: 'Ninham Country Holidays',
        url: 'https://www.pitchup.com/campsites/England/South_East/Isle_Of_Wight/Shanklin/ninham-country-holidays/',
      },
      {
        name: 'Hale Farm Campsite',
        url: 'https://www.pitchup.com/campsites/England/South_East/East_Sussex/chiddingly/hale-farm-campsite/',
      },
      {
        name: 'Kings Barn Camping',
        url: 'https://www.pitchup.com/campsites/England/South_East/West_Sussex/Steyning/kings-barn-camping/',
      },
      {
        name: 'Ashdown Forest Riding Centre',
        url: 'https://www.pitchup.com/campsites/England/South_East/East_Sussex/Crowborough/ashdown-forest-riding-centre/',
      },
    ],
  },
  {
    areaTitle: 'Central England',
    area: 'central-england',
    intro:
      "Scoring a 9.2/10 overall, Central England offers a peaceful retreat with plenty of opportunities for outdoor activities. From the history of the Cotswolds to the Peak District's serene beauty, it's perfect for a relaxing getaway immersed in the heart of England.",
    winners: [
      {
        name: 'Bailey Bridge Farm',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/holland_fen/bailey_bridge_farm/',
      },
      {
        name: 'Rosedale',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/thorpe_st_peter/rosedale/',
      },
      {
        name: 'Offcote Grange',
        url: 'https://www.pitchup.com/campsites/England/Central/Derbyshire/Ashbourne/offcote-grange/',
      },
      {
        name: 'Severn Valley Touring Caravan and Camping Site',
        url: 'https://www.pitchup.com/campsites/England/Central/Gloucestershire/newnham/severn_valley_touring_caravan_camping_site/',
      },
      {
        name: 'Orchard View Caravan and Camping Park',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/Spalding/orchard-view-caravan-camping-park/',
      },
      {
        name: 'Pear Tree Cottage at Poplar Farm',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/Sutton_on_Sea/pear_tree_cottage_poplar_farm/',
      },
      {
        name: 'Pike Low Farm Camping',
        url: 'https://www.pitchup.com/campsites/England/Central/Staffordshire/broomyshaw/pike-low-farm-camping/',
      },
      {
        name: 'Little Hay Grange Wild Camping',
        url: 'https://www.pitchup.com/campsites/England/Central/Derbyshire/ockbrook/little-hay-grange-wild-camping/',
      },
      {
        name: 'Marshlands Alpacas',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/Barton-Upon-Humber/marshlands-alpacas/',
      },
      {
        name: 'Corve Barn Farm Mobile Home Park',
        url: 'https://www.pitchup.com/campsites/England/Central/Shropshire/Much_Wenlock/corve_barn_farm_mobile_home_park/',
      },
      {
        name: 'Lindsey Trail Touring Park',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/Market_Rasen/lindsey_trail_touring_park/',
      },
      {
        name: 'Oakhill Leisure',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/Lincoln/oakhill-leisure/',
      },
      {
        name: 'Tyre Farm',
        url: 'https://www.pitchup.com/campsites/England/Central/Staffordshire/Leek/bentygrange-caravan-site/',
      },
      {
        name: 'The Shires Park',
        url: 'https://www.pitchup.com/campsites/England/Central/Herefordshire/Hereford/the-shires-park/',
      },
      {
        name: 'Sunnyfield Culworth Campsite',
        url: 'https://www.pitchup.com/campsites/England/Central/Northamptonshire/culworth/sunnyfield-culworth-campsite/',
      },
      {
        name: 'Langdale Lakes',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/langworth/langdale-lakes/',
      },
      {
        name: 'Cresslands Touring Park',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/bourne/cresslands-touring-park/',
      },
      {
        name: 'Woodside Caravan Park',
        url: 'https://www.pitchup.com/campsites/England/Central/Lincolnshire/bonby/woodside/',
      },
      {
        name: 'Panpwnton Farm',
        url: 'https://www.pitchup.com/campsites/England/Central/Shropshire/llanfair-waterdine/panpwnton-farm/',
      },
      {
        name: 'The Old Bush',
        url: 'https://www.pitchup.com/campsites/England/Central/Worcestershire/Worcester/the_old_bush/',
      },
    ],
  },
  {
    areaTitle: 'South Wales',
    area: 'south-wales',
    intro:
      'Home to the creative capital city Cardiff and the serene Wye Valley, South Wales scored a high 9.2/10 overall from guests in 2023. The perfect blend of outdoor adventure, community and culture, our customers agree that South Wales is a top-notch holiday destination.',
    winners: [
      {
        name: 'Panoramic Camping and Glamping',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Swansea/panoramic-camping-glamping/',
      },
      {
        name: 'Tal-y-Fan Farm',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Bridgend/tal-y-fan-farm/',
      },
      {
        name: "The Hog's Head",
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/Abergavenny/the-hogs-head/',
      },
      {
        name: 'Beacons View Camping',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/Blackwood/beacons-view-camping/',
      },
      {
        name: 'Parc Y Deri Farm',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/raglan/parc-y-deri-farm/',
      },
      {
        name: 'Red Sky at Night Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/Monmouth/red-sky-at-night-campsite/',
      },
      {
        name: 'Our Welsh Caravan and Camping',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Bridgend/our-welsh-caravan-and-camping/',
      },
      {
        name: 'Wenallt Wild Camp',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Neath/wenallt-wild-camp/',
      },
      {
        name: 'Hendre Farmhouse Orchard Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/Monmouth/hendre-farmhouse-orchard-campsite/',
      },
      {
        name: 'Brynglas Retreat',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/gwaun_cae_gurwen/brynglas_retreat/',
      },
      {
        name: 'Red Rock Wild Camping',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Port_Talbot/graig-goch-wild-camping/',
      },
      {
        name: 'Eco Camping Wales',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Swansea/eco_camping_wales/',
      },
      {
        name: 'Pool Farm',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Cowbridge/pool-farm/',
      },
      {
        name: 'Greenways of Gower Premier Leisure Park',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Swansea/greenways-of-gower-premier-leisure-park/',
      },
      {
        name: 'Dare Valley Country Park',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Aberdare/dare-valley-country-park/',
      },
      {
        name: 'Bryn Bettws Lodge',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Port_Talbot/bryn-bettws-lodge/',
      },
      {
        name: 'Cae Glas Camping',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Swansea/cae-glas-camping/',
      },
      {
        name: 'Cwmsoar Camping',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Monmouthshire/Pontypool/cwmsoar-camping/',
      },
      {
        name: 'Tafarn-Y-Garreg Campsite',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/pen-y-cae/tafarn_y_garreg_campsite/',
      },
      {
        name: 'Fferm Gelli Camping',
        url: 'https://www.pitchup.com/campsites/Wales/South-Wales/Glamorgan/Caerphilly/fferm-gelli-camping/',
      },
    ],
  },
  {
    areaTitle: 'Scotland',
    area: 'scotland',
    intro:
      'Scotland scored a high 9.2 in 2023 with Pitchup customers. From misty lochs to rolling Highlands, Scotland offers a unique blend of breathtaking scenery and rich cultural heritage, perfect for nature lovers and adventurers alike.',
    winners: [
      {
        name: 'Drummohr Camping and Glamping Site',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Lothian/Edinburgh/drummohr-caravan-park/',
      },
      {
        name: 'Woodend Caravan and Camping Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Highlands/Lairg/woodend-caravan-camping-park/',
      },
      {
        name: 'Isle Of Iona Campsite',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Argyll/isle-iona/isle-of-iona-campsite/',
      },
      {
        name: 'Anwoth Caravan Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Dumfries_and_Galloway/Gatehouse_of_Fleet/anwoth-caravan-park/',
      },
      {
        name: 'Woodland Gardens Caravan Site',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Fife/Leven/woodland-gardens-caravan-site/',
      },
      {
        name: 'East Balthangie Caravan Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Aberdeenshire/Turriff/east-balthangie-caravan-park/',
      },
      {
        name: "Balloch O'Dee Campsite",
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Dumfries_and_Galloway/Newton_Stewart/balloch-odee-campsite-and-trekking-centre/',
      },
      {
        name: 'The Wee Caravan Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Ayrshire/New_Cumnock/the-wee-caravan-park/',
      },
      {
        name: 'Barrow Campsite',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Highlands/Nairn/barns_campsite/',
      },
      {
        name: 'Nethercraig Holiday Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Perthshire/Blairgowrie/nethercraig_holiday_park/',
      },
      {
        name: 'Dall Cottage Camping',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Perthshire/Pitlochry/dall_cottage_camping/',
      },
      {
        name: 'Kirkton Holidays',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Highlands/Melvich/kirkton-holidays/',
      },
      {
        name: 'Glenshee Glamping',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Perthshire/Blairgowrie/glenshee-glamping/',
      },
      {
        name: 'Black Rock Caravan and Camping Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Highlands/Evanton/black-rock-caravan-camping-park/',
      },
      {
        name: 'Foulden Hagg',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Berwickshire/foulden/foulden_hagg/',
      },
      {
        name: 'Firbank Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Aberdeenshire/Aboyne/firbank-park/',
      },
      {
        name: 'Blair Drummond Caravan Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Stirlingshire/Blair_Drummond/blair-drummond-caravan-park/',
      },
      {
        name: 'Auchingarrich Wildlife Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Perthshire/Comrie/auchingarrich-wildlife-park/',
      },
      {
        name: 'Heads of Ayr Holiday Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Ayrshire/Ayr/heads-of-ayr-caravan-park/',
      },
      {
        name: 'Seaward Holiday Park',
        url: 'https://www.pitchup.com/campsites/Scotland/Scotland/Dumfries_and_Galloway/Kirkcudbright/seaward-holiday-park/',
      },
    ],
  },
]
</script>
<style>
@import url('~/assets/css/review-awards.css');
</style>
<style scoped>
.review-awards.y2023 .hero {
  background: var(--colour-page-background)
    url('~/assets/images/review-awards-hero.jpg') no-repeat left 45%;
  background-size: cover;
}

.review-awards .top-5 h2 {
  color: white;
  margin: 0 0 0.5em 0;
}

.review-awards .category-icons {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 0.25em;
}

.review-awards .site-category {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: space-between;
  min-width: 1.4em;
}

.desktop.review-awards .category-icons {
  gap: 0.5em;
}

.review-awards .category-icon {
  height: 1em;
  width: auto;
}

.desktop.review-awards .category-icon {
  height: 1.8em;
}

.review-awards .count {
  color: var(--colour-text-light);
  font-size: var(--small);
  text-align: center;
}
</style>
